/**
 * Block → markdown line-number map.
 *
 * Maps each block in an AST to the line range it occupies in
 * `astToMarkdown(ast, { mode: 'author', includeMetadata: true })` — the exact
 * rendering written to `content/<file>.md` on disk (see
 * `server/src/services/contentFileIO.ts` `writeSourceFileToDisk`).
 *
 * Used by the editor's line-number gutter so the numbers shown next to each
 * block match the line numbers of the on-disk source file. Because it builds on
 * the same renderer exports (`astToMarkdown` / `blockToMarkdown`), it tracks the
 * renderer automatically — `blockLineNumbers.test.ts` locks the two together.
 */

import type { ContentAST } from './astTypes'
import { astToMarkdown, blockToMarkdown } from './markdownRenderer'

export interface BlockLineInfo {
  id: string
  /** 1-based line where this block's markdown begins. */
  startLine: number
  /** 1-based line where this block's markdown ends (inclusive). */
  endLine: number
}

/** Number of lines a string occupies (a string with no newline is 1 line). */
const lineCount = (s: string): number => s.split('\n').length

/**
 * Compute the line range of every block as it appears in the author-mode
 * markdown with frontmatter. Mirrors `MarkdownRenderer.render`: frontmatter
 * first, then blocks, joined with a blank line (`\n\n`) between each part.
 */
export function computeBlockLineNumbers(ast: ContentAST): BlockLineInfo[] {
  // Frontmatter rendered identically to the real output (blocks don't affect it).
  const frontmatter = astToMarkdown({ ...ast, blocks: [] }, { mode: 'author', includeMetadata: true })

  // `cursor` tracks the last line number consumed so far.
  let cursor = lineCount(frontmatter)
  const result: BlockLineInfo[] = []

  for (const block of ast.blocks) {
    const md = blockToMarkdown(block, { mode: 'author' })
    // The `\n\n` separator before this part adds one blank line, so the block's
    // first line lands two lines past the previous part's last line.
    const startLine = cursor + 2
    const endLine = startLine + lineCount(md) - 1
    result.push({ id: block.id, startLine, endLine })
    cursor = endLine
  }

  return result
}
